import mitt from "mitt";

export interface GuestAudioSource {
  deviceId: string;

  label: string;

  stream: MediaStream;

  active: boolean;
}

type GuestAudioEvents = {
  updated: void;
};

/**
 * Singleton manager that tracks connected guest audio streams.
 * When a guest connects via WebRTC, its audio stream is registered here
 * so the Microphones panel can display it as a selectable audio source.
 */

class GuestAudioManager {
  private sources = new Map<string, GuestAudioSource>();

  private emitter = mitt<GuestAudioEvents>();

  /**
   * Register a guest's audio stream.
   * Called when a guest widget detects a connected WebRTC stream with audio tracks.
   */

  register(deviceId: string, label: string, stream: MediaStream) {
    const audioTracks = stream.getAudioTracks();

    if (audioTracks.length === 0) return;

    // Preserve active state if re-registering (e.g. stream refresh)

    const existing = this.sources.get(deviceId);

    this.sources.set(deviceId, {
      deviceId,

      label,

      stream,

      active: existing?.active ?? false,
    });

    this.emitter.emit("updated");
  }

  /**
   * Update the label for a registered guest (e.g. when resolved label changes).
   */

  updateLabel(deviceId: string, label: string) {
    const source = this.sources.get(deviceId);

    if (source && source.label !== label) {
      source.label = label;

      this.emitter.emit("updated");
    }
  }

  /**
   * Remove a guest's audio stream (e.g. on disconnect or widget deletion).
   */

  unregister(deviceId: string) {
    if (this.sources.has(deviceId)) {
      this.sources.delete(deviceId);

      this.emitter.emit("updated");
    }
  }

  /**
   * Mark a guest audio source as active (routed into the audio mix).
   */

  setActive(deviceId: string, active: boolean) {
    const source = this.sources.get(deviceId);

    if (source) {
      source.active = active;

      this.emitter.emit("updated");
    }
  }

  /**
   * Get all registered guest audio sources.
   */

  getAll(): GuestAudioSource[] {
    return Array.from(this.sources.values());
  }

  /**
   * Get a specific guest audio source.
   */

  get(deviceId: string): GuestAudioSource | undefined {
    return this.sources.get(deviceId);
  }

  /**
   * Check if a guest audio source is registered.
   */

  has(deviceId: string): boolean {
    return this.sources.has(deviceId);
  }

  on(handler: () => void): () => void {
    this.emitter.on("updated", handler);

    return () => this.emitter.off("updated", handler);
  }

  destroy() {
    this.sources.clear();

    this.emitter.all.clear();
  }
}

export default new GuestAudioManager();
